import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { CurrencySelect } from '@/components/selects';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { financeApi } from '@/api/finance.api';
import { customersApi } from '@/api/customers.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinInvoice } from '@/types/finance';
import type { CustomerResponse } from '@/types/customer';
import { InvoiceSendModal } from '@/modules/finance/invoices/InvoiceSendModal';
import { buildDraftInvoicePreviewHtml } from '@/modules/finance/invoices/invoicePreviewHtml';

type LineDraft = {
  lineDescription: string;
  quantity: string;
  unitPrice: string;
  taxAmount: string;
};

type SendModalState = {
  invoiceId?: number;
  draftHtml?: string;
  defaultEmail?: string;
};

const emptyLine = (): LineDraft => ({
  lineDescription: '',
  quantity: '1',
  unitPrice: '0',
  taxAmount: '0',
});

export function FinanceInvoicesPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('FINANCE_INVOICE_MANAGE', 'FINANCE_MANAGE');
  const pagination = usePagination();
  const [invoices, setInvoices] = useState<FinInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOnCreate, setSendOnCreate] = useState(false);
  const [sendModal, setSendModal] = useState<SendModalState | null>(null);
  const [customerId, setCustomerId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => String(c.id) === customerId),
    [customers, customerId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await financeApi.listInvoices({ page: pagination.page, size: pagination.size });
      setInvoices(r.content);
      pagination.applyPageResponse(r);
    } catch (e) {
      showApiError(e, 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
    customersApi
      .list({ page: 0, size: 100 })
      .then((r) => setCustomers(r.content))
      .catch(() => setCustomers([]));
  }, [load]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const line of lines) {
      const qty = Number(line.quantity) || 0;
      const unit = Number(line.unitPrice) || 0;
      const lineTax = Number(line.taxAmount) || 0;
      subtotal += qty * unit;
      tax += lineTax;
    }
    const discount = Number(discountAmount) || 0;
    return { subtotal, tax, discount, total: subtotal + tax - discount };
  }, [lines, discountAmount]);

  const validLines = useMemo(
    () =>
      lines
        .filter((l) => l.lineDescription.trim())
        .map((l) => ({
          lineDescription: l.lineDescription.trim(),
          quantity: Number(l.quantity) || 1,
          unitPrice: Number(l.unitPrice) || 0,
          taxAmount: Number(l.taxAmount) || 0,
        })),
    [lines],
  );

  const resetForm = () => {
    setCustomerId('');
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setDueDate(d.toISOString().slice(0, 10));
    setCurrencyCode('USD');
    setDiscountAmount('0');
    setNotes('');
    setLines([emptyLine()]);
    setSendOnCreate(false);
  };

  const validateForm = (): boolean => {
    if (totals.total <= 0) {
      notify.error('Invoice total must be greater than zero');
      return false;
    }
    if (validLines.length === 0) {
      notify.error('Add at least one line item');
      return false;
    }
    if (sendOnCreate && !customerId) {
      notify.error('Select a customer to email the invoice');
      return false;
    }
    return true;
  };

  const buildDraftPreview = () => {
    if (!validateForm()) return;
    setSendModal({
      draftHtml: buildDraftInvoicePreviewHtml({
        invoiceNumber: invoiceNumber.trim() || undefined,
        invoiceDate,
        dueDate,
        currencyCode,
        customerName: selectedCustomer?.name,
        customerEmail: selectedCustomer?.email,
        notes: notes.trim() || undefined,
        discountAmount: totals.discount,
        lines: validLines,
        subtotal: totals.subtotal,
        tax: totals.tax,
        total: totals.total,
      }),
      defaultEmail: selectedCustomer?.email,
    });
  };

  const createInvoice = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const emailForSend = selectedCustomer?.email;
      const openSendAfterCreate = sendOnCreate;
      const created = await financeApi.createInvoice({
        customerId: customerId ? Number(customerId) : undefined,
        invoiceNumber: invoiceNumber.trim() || undefined,
        invoiceDate,
        dueDate,
        currencyCode,
        discountAmount: totals.discount,
        notes: notes.trim() || undefined,
        lines: validLines,
      });
      notify.success('Invoice created');
      setShowForm(false);
      resetForm();
      await load();

      if (openSendAfterCreate) {
        setSendModal({
          invoiceId: created.id,
          defaultEmail: emailForSend,
        });
      }
    } catch (err) {
      showApiError(err, 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (inv: FinInvoice) => {
    try {
      const { blob, filename } = await financeApi.downloadInvoicePdf(inv.id, inv.invoiceNumber);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showApiError(err, 'PDF download failed');
    }
  };

  const printInvoice = async (id: number) => {
    try {
      await financeApi.openInvoicePrint(id);
    } catch (err) {
      showApiError(err, 'Print failed');
    }
  };

  const openSendPreview = (inv: FinInvoice) => {
    setSendModal({ invoiceId: inv.id });
  };

  return (
    <div>
      <PageHeader
        title="Invoices"
        description="Create customer invoices, preview, and email PDF to customers"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Invoices' }]}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Close' : 'New invoice'}
            </Button>
          ) : undefined
        }
      />

      {canManage && showForm && (
        <form className="card p-4 mb-4 space-y-4" onSubmit={createInvoice}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Create invoice</h3>
            <Button type="button" variant="secondary" onClick={buildDraftPreview}>
              Preview draft
            </Button>
          </div>

          <FormGrid columns={4}>
            <Select
              label="Customer"
              placeholder="Select customer"
              options={customers.map((c) => ({
                value: String(c.id),
                label: `${c.name}${c.email ? ` (${c.email})` : ''}`,
              }))}
              value={customerId}
              onChange={setCustomerId}
            />
            <Input
              label="Invoice # (optional)"
              placeholder="Auto-generated if blank"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
            <DatePicker label="Invoice date" value={invoiceDate} onChange={setInvoiceDate} />
            <DatePicker label="Due date" value={dueDate} onChange={setDueDate} />
            <CurrencySelect label="Currency" value={currencyCode} onChange={setCurrencyCode} />
            <Input
              type="number"
              step="0.01"
              label="Discount"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
            />
          </FormGrid>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-right w-24">Qty</th>
                  <th className="p-2 text-right w-28">Unit price</th>
                  <th className="p-2 text-right w-24">Tax</th>
                  <th className="p-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, idx) => (
                  <tr key={idx} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="p-2">
                      <input
                        className="input w-full"
                        value={line.lineDescription}
                        onChange={(e) => {
                          const next = [...lines];
                          next[idx] = { ...line, lineDescription: e.target.value };
                          setLines(next);
                        }}
                        placeholder="Service or product"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="input w-full text-right"
                        type="number"
                        step="0.01"
                        value={line.quantity}
                        onChange={(e) => {
                          const next = [...lines];
                          next[idx] = { ...line, quantity: e.target.value };
                          setLines(next);
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="input w-full text-right"
                        type="number"
                        step="0.01"
                        value={line.unitPrice}
                        onChange={(e) => {
                          const next = [...lines];
                          next[idx] = { ...line, unitPrice: e.target.value };
                          setLines(next);
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="input w-full text-right"
                        type="number"
                        step="0.01"
                        value={line.taxAmount}
                        onChange={(e) => {
                          const next = [...lines];
                          next[idx] = { ...line, taxAmount: e.target.value };
                          setLines(next);
                        }}
                      />
                    </td>
                    <td className="p-2 text-right">
                      {lines.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-3 items-center justify-between">
            <Button type="button" variant="secondary" onClick={() => setLines([...lines, emptyLine()])}>
              Add line
            </Button>
            <div className="text-sm text-right space-y-1">
              <div>Subtotal: <span className="font-mono">{totals.subtotal.toFixed(2)}</span></div>
              <div>Tax: <span className="font-mono">{totals.tax.toFixed(2)}</span></div>
              <div className="font-semibold">
                Total: <span className="font-mono">{totals.total.toFixed(2)} {currencyCode}</span>
              </div>
            </div>
          </div>

          <Input label="Notes (shown on invoice)" value={notes} onChange={(e) => setNotes(e.target.value)} />

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 rounded border-slate-300"
              checked={sendOnCreate}
              onChange={(e) => setSendOnCreate(e.target.checked)}
            />
            <span>
              <span className="font-medium">Email customer after creating</span>
              <span className="block text-slate-500 text-xs mt-0.5">
                Opens preview &amp; send step after save (requires customer; you confirm before email is sent).
              </span>
            </span>
          </label>

          {sendOnCreate && customerId && !selectedCustomer?.email && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              This customer has no email on file — you can enter one in the send preview step.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={saving}>
              {sendOnCreate ? 'Create & preview send' : 'Create invoice'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowForm(false); resetForm(); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50 dark:bg-slate-800/50">
              <th className="p-2 text-left">Number</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Due</th>
              <th className="p-2 text-right">Total</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Sent</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-2 font-mono">{inv.invoiceNumber}</td>
                <td className="p-2">{inv.invoiceDate}</td>
                <td className="p-2">{inv.dueDate ?? '—'}</td>
                <td className="p-2 text-right font-mono">{inv.totalAmount} {inv.currencyCode}</td>
                <td className="p-2">{inv.statusCode}</td>
                <td className="p-2 text-xs text-slate-500">{inv.sentTo ?? 'Not sent'}</td>
                <td className="p-2 text-right whitespace-nowrap space-x-2">
                  {canManage && (
                    <button
                      type="button"
                      className="text-primary-600 text-xs hover:underline"
                      onClick={() => openSendPreview(inv)}
                    >
                      Preview &amp; send
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-slate-600 text-xs hover:underline"
                    onClick={() => void downloadPdf(inv)}
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    className="text-slate-600 text-xs hover:underline"
                    onClick={() => void printInvoice(inv.id)}
                  >
                    Print
                  </button>
                </td>
              </tr>
            ))}
            {!loading && invoices.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-slate-500">
                  No invoices yet. Create one to bill a customer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={invoices.length > 0}
        />
      </div>

      <InvoiceSendModal
        open={sendModal != null}
        onClose={() => setSendModal(null)}
        invoiceId={sendModal?.invoiceId}
        draftHtml={sendModal?.draftHtml}
        defaultEmail={sendModal?.defaultEmail}
        onSent={() => void load()}
      />
    </div>
  );
}
